import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {Box, CircularProgress, Stack, styled} from '@mui/material';
import {
  Body,
  designSystemToken,
  Headline,
  Icon,
} from '@lightricks/react-design-system';
import LtxScalerProjectLoader from '@contentlab/components/ltx-scaler-project-loader';
import contentLabStore, {
  contentLabActions,
} from '@contentlab/stores/contentLabStore';
import {CaptionsData, StyleConfig} from '@contentlab/types/captions';
import Skeleton from '@/components/skeleton';
import useElementDimensions from '@/hooks/use-element-dimensions';
import VideoCaptionOverlayStandalone from './captions-rendering/VideoCaptionOverlayStandalone';
import {VolumeControl, FullscreenToggle} from './controls';

// eslint-disable-next-line @typescript-eslint/naming-convention
export enum PlayToggleType {
  CONTROLLED = 'controlled',
  UNCONTROLLED = 'uncontrolled',
}

export enum Control {
  VOLUME = 'volume',
  TOGGLE_FULLSCREEN = 'toggle_fullscreen',
}

interface VideoDisplayProps {
  testID?: string;
  id?: string;
  videoUrl: string;
  videoName: string | React.ReactNode;
  videoSubtitle?: string | React.ReactNode;
  subtitleTime: number;
  currentIndex?: string | null;
  onPlayToggle?: (isPlaying: boolean, type: PlayToggleType) => void;
  onTimeChange?: (time: number, percentage: number) => void;
  onTimeUpdate?: (time: number) => void;
  actionComponent?: React.ReactNode;
  enableKeyboardControls?: boolean;
  autofocus?: boolean;
  isPlayingState?: {
    isPlaying: boolean;
    setIsPlaying: (isPlaying: boolean) => void;
  }; // Used to control the video playback from the parent component.
  videoVolume?: number; // Volume for the video (0.0 to 1.0)
  resetOnStop?: boolean; // Whether to reset video to beginning when stopping
  disableSeeking?: boolean; // Whether to disable progress bar seeking
  playingDisabled?: boolean; // Whether to disable play/pause interactions
  loadingType?: 'spinner' | 'shimmer';
  showLoader?: boolean;
  captionsData?: CaptionsData;
  captionStyle?: StyleConfig;
  showPauseOverlay?: boolean;
  cursorStyle?: 'pointer' | 'default';
  renderVideoOverlay?: (
    videoRef: React.RefObject<HTMLVideoElement>
  ) => React.ReactNode;
  controls?: Control[];
  allowVolumeControl?: boolean;
  theme?: 'dark' | 'light';
  isOutputLoading?: boolean;
  progressPercentage?: number | null;
  hasExceededEstimate?: boolean;
}

interface ColorScheme {
  containerBorderColor: string;
  videoNameColor: string;
  backgroundColor: string;
  controlsColor: string;
  progressTrackColor: string;
  progressColor: string;
  controlsDisabledColor: string;
}

const getColorScheme = () => ({
  dark: {
    containerBorderColor: designSystemToken('semantic.bg.neutral-secondary'),
    videoNameColor: designSystemToken('semantic.fg.inverse'),
    backgroundColor: designSystemToken('semantic.fg.primary'),
    controlsColor: designSystemToken(`semantic.bg.secondary`),
    progressTrackColor: designSystemToken('semantic.fg.inverse-secondary'),
    progressColor: designSystemToken(`semantic.bg.secondary`),
    controlsDisabledColor: designSystemToken('semantic.fg.disabled'),
  },
  light: {
    containerBorderColor: designSystemToken('semantic.bg.neutral-secondary'),
    videoNameColor: designSystemToken('semantic.fg.primary'),
    backgroundColor: designSystemToken(`semantic.bg.secondary`),
    controlsColor: designSystemToken('semantic.fg.primary'),
    progressTrackColor: designSystemToken('semantic.bg.neutral-inverse'),
    progressColor: designSystemToken('semantic.fg.primary'),
    controlsDisabledColor: designSystemToken('semantic.fg.disabled'),
  },
});

const shouldNotForwardProps = ['colorScheme'];

const Container = styled(Stack, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})(({colorScheme}: {colorScheme: ColorScheme}) => ({
  boxSizing: 'border-box',
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  backgroundColor: colorScheme.backgroundColor,
  border: `1px solid ${colorScheme.containerBorderColor}`,
  borderRadius: '32px',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  cursor: 'pointer',
  gap: '24px',
  outline: 'unset',
}));

const LoaderContainer = styled(Box)(() => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 1,
}));

const VideoContainer = styled(Stack, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})(({colorScheme}: {colorScheme: ColorScheme}) => ({
  boxSizing: 'border-box',
  padding: '24px 0 24px 0',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
  flex: 1,
  width: '100%',
  height: '100%',
  background: colorScheme.backgroundColor,
  justifyContent: 'space-between',
}));

const VideoHeader = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  boxSizing: 'border-box',
  paddingInline: '24px',
  width: '100%',
  minHeight: '40px',
}));

const VideoHeaderInfo = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  flex: 1,
  minWidth: 0,
}));

const HeaderInfoContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  flex: 1,
  minWidth: 0,
}));

const ControlsRow = styled(Stack)(() => ({
  width: '100%',
  boxSizing: 'border-box',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingInline: '24px',
  gap: '4px',
}));

const PlayIconContainer = styled(Box)(() => ({
  alignItems: 'center',
}));

const ProgressBarContainer = styled(Box)(() => ({
  'flex': 1,
  'borderRadius': '999px',
  'padding': '8px 0 ',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
}));

const ProgressBar = styled(Box, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})(({colorScheme}: {colorScheme: ColorScheme}) => ({
  flex: 1,
  height: '4px',
  borderRadius: '999px',
  backgroundColor: colorScheme.progressTrackColor,
  marginInline: '12px',
}));

const ProgressFill = styled(Box, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})<{width: string; colorScheme: ColorScheme}>(({width, colorScheme}) => ({
  width,
  height: '100%',
  backgroundColor: colorScheme.progressColor,
  borderRadius: '999px',
}));

const TimeInfoContainer = styled(Body)(() => ({
  minWidth: '65px',
}));

const VideoElementContainer = styled(Stack, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})(({colorScheme}: {colorScheme: ColorScheme}) => ({
  position: 'relative',
  width: '100%',
  flex: '1 1 0',
  minHeight: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: colorScheme.backgroundColor,
  borderTop: `1px solid ${colorScheme.containerBorderColor}`,
  borderBottom: `1px solid ${colorScheme.containerBorderColor}`,
  padding: '16px',
  boxSizing: 'border-box',
}));

const VideoContentWrapper = styled('div')<{
  isOutputLoading: boolean;
  colorScheme: ColorScheme;
}>(({isOutputLoading, colorScheme}) => ({
  'position': 'relative',
  'display': 'inline-flex',
  'maxWidth': '100%',
  'maxHeight': '100%',
  'overflow': 'hidden',
  'borderRadius': '8px',
  'backgroundColor': isOutputLoading
    ? colorScheme.backgroundColor
    : 'transparent',
  'transition': 'background-color 0.3s ease-in-out',

  '&::before': isOutputLoading
    ? {
        content: '""',
        position: 'absolute',
        inset: 0,
        backdropFilter: isOutputLoading ? 'blur(40px)' : 'blur(0px)',
        WebkitBackdropFilter: isOutputLoading ? 'blur(40px)' : 'blur(0px)', // Safari support
        borderRadius: '8px',
        backgroundColor: 'rgba(255, 255, 255, 0.01)',
        zIndex: 10,
        pointerEvents: 'none',
        opacity: isOutputLoading ? 1 : 0,
        transition:
          'opacity 0.7s ease-in-out, backdrop-filter 0.7s ease-in-out',
      }
    : {},
}));

const VideoElement = styled('video')<{
  isLoading: boolean;
  isOutputLoading: boolean;
}>(({isLoading, isOutputLoading}) => ({
  display: isLoading ? 'none' : 'block',
  maxWidth: '100%',
  maxHeight: '100%',
  width: 'auto',
  height: 'auto',
  transform: isOutputLoading ? 'scale(1.1)' : 'scale(1)',
  transition: 'transform 0.7s ease-in-out',
}));

const VideoElementOverlay = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isVisible',
})<{isVisible: boolean}>(({isVisible}) => ({
  position: 'absolute',
  inset: 0,
  backgroundColor: 'rgb(0, 0, 0, 0.3)',
  zIndex: 1,
  opacity: isVisible ? 1 : 0,
  visibility: isVisible ? 'visible' : 'hidden',
  transition: 'visibility 0.3s ease-out, opacity 0.3s ease-out',
  pointerEvents: 'none',
}));

const VideoElementPlayIconOverlay = styled(Box)(() => ({
  'position': 'absolute',
  'top': '50%',
  'left': '50%',
  'transform': 'translate(-50%, -50%)',
  'zIndex': 11,
  'cursor': 'pointer',
  'display': 'flex',
  'alignItems': 'center',
  'justifyContent': 'center',
  'transition':
    'visibility 0.3s ease-out, opacity 0.3s ease-out, transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translate(-50%, -50%) scale(1.1)',
  },
}));

function VideoDisplay({
  testID = 'video-display',
  id,
  videoUrl,
  videoName,
  videoSubtitle,
  subtitleTime,
  currentIndex,
  onPlayToggle,
  onTimeChange,
  onTimeUpdate,
  actionComponent,
  enableKeyboardControls = true,
  autofocus = true,
  isPlayingState = undefined,
  videoVolume = 1.0,
  resetOnStop = false,
  disableSeeking = false,
  playingDisabled = false,
  loadingType = 'spinner',
  showLoader = false,
  captionsData,
  captionStyle,
  showPauseOverlay = true,
  cursorStyle = 'pointer',
  renderVideoOverlay,
  controls = [Control.VOLUME, Control.TOGGLE_FULLSCREEN],
  allowVolumeControl = false,
  theme = 'light',
  isOutputLoading = false,
  progressPercentage,
  hasExceededEstimate = false,
}: VideoDisplayProps) {
  const {elementRef: containerRef} = useElementDimensions([], true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const INITIAL_VIDEO_TIMESTAMP = '00:00/00:00';

  const colorScheme = getColorScheme()[theme];

  const [fallbackIsPlaying, fallbackSetIsPlaying] = useState(false);
  const isPlaying: boolean = isPlayingState?.isPlaying ?? fallbackIsPlaying;
  const setIsPlaying: (isPlaying: boolean) => void =
    isPlayingState?.setIsPlaying ?? fallbackSetIsPlaying;

  const togglePlay = useCallback(
    (event?: React.MouseEvent<HTMLElement>) => {
      event?.stopPropagation();
      if (playingDisabled || isLoading || isOutputLoading) {
        return;
      }
      if (videoRef.current) {
        setIsPlaying(!isPlaying);
        onPlayToggle?.(!isPlaying, PlayToggleType.CONTROLLED);
      }
    },
    [
      isPlaying,
      setIsPlaying,
      videoRef,
      playingDisabled,
      onPlayToggle,
      isLoading,
      isOutputLoading,
    ]
  );

  const handlePlayPromise = useCallback(
    (
      playPromise: Promise<void> | undefined,
      shouldSilentlyIgnoreErrors = false
    ) => {
      playPromise?.catch((error) => {
        if (!shouldSilentlyIgnoreErrors) {
          throw error;
        }
      });
    },
    []
  );

  useEffect(() => {
    let isCancelled = false;

    if (videoRef.current && !isLoading) {
      if (isOutputLoading) {
        // Background video - always silently ignore errors
        handlePlayPromise(videoRef.current.play(), true);
      } else if (isPlaying) {
        // Regular video - only silently ignore if component is cancelled
        handlePlayPromise(videoRef.current.play(), isCancelled);
        onPlayToggle?.(true, PlayToggleType.UNCONTROLLED);
      } else {
        videoRef.current.pause();
        if (resetOnStop) {
          videoRef.current.currentTime = 0;
        }
      }
    }

    // Cleanup: prevent error logging if component unmounts or effect re-runs
    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, videoRef, resetOnStop, isLoading, isOutputLoading]);

  const {isMuted} = contentLabStore(
    useShallow((state) => ({
      isMuted: state.isMuted,
    }))
  );

  const toggleMute = useCallback(
    (event?: React.MouseEvent<HTMLDivElement>) => {
      event?.stopPropagation();
      if (isOutputLoading) {
        return;
      }
      contentLabActions.setIsMuted(!isMuted);
    },
    [isMuted, isOutputLoading]
  );

  const toggleFullscreen = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      if (isOutputLoading) {
        return;
      }
      if (!controls?.includes(Control.TOGGLE_FULLSCREEN)) {
        return;
      }
      if (!document.fullscreenElement) {
        // Enter fullscreen
        if (containerRef.current?.requestFullscreen) {
          containerRef.current.requestFullscreen();
        }
      } else if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    },
    [containerRef, controls, isOutputLoading]
  );

  const handleDoubleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      toggleFullscreen(event);
    },
    [toggleFullscreen]
  );

  const handleVolumeChange = useCallback(
    (volume: number) => {
      if (videoRef.current) {
        videoRef.current.volume = volume;
        if (isMuted) {
          toggleMute();
        }
      }
    },
    [isMuted, toggleMute]
  );

  const renderControl = useCallback(
    (control: Control) => {
      switch (control) {
        case Control.VOLUME:
          return (
            <Box
              key="volume"
              sx={{
                opacity: isOutputLoading ? 0.48 : 1,
                cursor: isOutputLoading ? 'default' : 'pointer',
                pointerEvents: isOutputLoading ? 'none' : 'auto',
              }}
            >
              <VolumeControl
                isMuted={isMuted}
                onToggleMute={toggleMute}
                videoVolume={videoVolume}
                onVolumeChange={handleVolumeChange}
                allowVolumeControl={allowVolumeControl}
                theme={theme}
                color={
                  isOutputLoading
                    ? colorScheme.controlsDisabledColor
                    : colorScheme.controlsColor
                }
              />
            </Box>
          );
        case Control.TOGGLE_FULLSCREEN:
          return (
            <Box
              key="fullscreen"
              sx={{
                color: isOutputLoading
                  ? colorScheme.controlsDisabledColor
                  : 'inherit',
                opacity: isOutputLoading ? 0.48 : 1,
                cursor: isOutputLoading ? 'default' : 'pointer',
                pointerEvents: isOutputLoading ? 'none' : 'auto',
              }}
            >
              <FullscreenToggle
                isFullscreen={isFullscreen}
                onToggleFullscreen={toggleFullscreen}
                color={
                  isOutputLoading
                    ? colorScheme.controlsDisabledColor
                    : colorScheme.controlsColor
                }
              />
            </Box>
          );
        default:
          return null;
      }
    },
    [
      isMuted,
      toggleMute,
      isFullscreen,
      toggleFullscreen,
      videoVolume,
      handleVolumeChange,
      allowVolumeControl,
      isOutputLoading,
    ]
  );

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isOutputLoading || isMuted;
    }
  }, [isMuted, isOutputLoading, videoRef]);

  // Control video volume
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = videoVolume;
    }
  }, [videoVolume, videoRef]);

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      onTimeUpdate?.(videoRef.current.currentTime);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (disableSeeking || isOutputLoading) {
      return; // Don't allow seeking when disabled
    }
    if (videoRef.current) {
      const progressBar = event.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      const clickPosition = event.clientX - rect.left;
      const percentage = (clickPosition / rect.width) * 100;
      const newTime = (percentage / 100) * videoRef.current.duration;
      changeTime(newTime);
      onTimeChange?.(newTime, percentage);
    }
  };

  const changeTime = (time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
    }
  };

  useEffect(() => {
    if (currentIndex) {
      changeTime(subtitleTime);
    }
  }, [subtitleTime, currentIndex]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!enableKeyboardControls || isOutputLoading) {
        return;
      }
      event.stopPropagation();
      if (event.key === ' ') {
        event.preventDefault();
        togglePlay();
      } else if (!disableSeeking) {
        // Only allow seeking if not disabled
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          if (videoRef.current) {
            changeTime(videoRef.current.currentTime + 5);
          }
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          if (videoRef.current) {
            changeTime(videoRef.current.currentTime - 5);
          }
        }
      }
    },
    [enableKeyboardControls, togglePlay, disableSeeking, isOutputLoading]
  );

  useEffect(() => {
    if (autofocus && containerRef.current) {
      containerRef.current.focus();
    }
  }, [autofocus, containerRef]);

  return (
    <Container
      id={id}
      data-testid={testID}
      ref={containerRef}
      onClick={togglePlay}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      sx={{cursor: isOutputLoading ? 'default' : cursorStyle}}
      colorScheme={colorScheme}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <VideoContainer colorScheme={colorScheme}>
        <VideoHeader>
          <VideoHeaderInfo>
            <Stack
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              gap="24px"
            >
              <HeaderInfoContainer>
                <Headline
                  size="xs"
                  color={colorScheme.videoNameColor}
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  flex={1}
                  minWidth={0}
                >
                  {videoName}
                </Headline>
                {videoSubtitle}
              </HeaderInfoContainer>
              {isFullscreen ? null : actionComponent}
            </Stack>
          </VideoHeaderInfo>
        </VideoHeader>
        {(isLoading || showLoader) &&
          (loadingType === 'shimmer' ? (
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              animation="wave"
              sx={{
                'marginBottom': '46px',
                '&.MuiSkeleton-wave::after': {
                  background:
                    'linear-gradient(to left, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0) 60%)',
                },
              }}
            />
          ) : (
            <LoaderContainer>
              <CircularProgress sx={{width: '100px', height: '100px'}} />
            </LoaderContainer>
          ))}

        {videoUrl ? (
          <VideoElementContainer
            onDoubleClick={handleDoubleClick}
            colorScheme={colorScheme}
          >
            <VideoContentWrapper
              isOutputLoading={isOutputLoading}
              colorScheme={colorScheme}
            >
              <VideoElement
                ref={videoRef}
                isLoading={isLoading}
                isOutputLoading={isOutputLoading}
                loop={isOutputLoading}
                onTimeUpdate={handleTimeUpdate}
                onLoadedData={() => {
                  setIsLoading(false);
                }}
                onEnded={() => {
                  if (isOutputLoading) {
                    if (videoRef.current) {
                      changeTime(0);
                      handlePlayPromise(videoRef.current.play(), true);
                    }
                  } else {
                    setIsPlaying(false);
                    changeTime(0);
                    onPlayToggle?.(false, PlayToggleType.UNCONTROLLED);
                  }
                }}
              >
                <source src={videoUrl} />
              </VideoElement>

              {isOutputLoading && (
                <LtxScalerProjectLoader
                  isVisible={isOutputLoading}
                  textMessages={[
                    'Analyzing your video...',
                    'Optimizing for your goal...',
                    'Finalizing content...',
                  ]}
                  progressPercentage={progressPercentage ?? null}
                  hasExceededEstimate={hasExceededEstimate}
                />
              )}

              {renderVideoOverlay?.(videoRef)}
              {captionsData?.captions &&
                captionsData.captions.length > 0 &&
                !isLoading &&
                !isOutputLoading &&
                videoRef.current && (
                  <VideoCaptionOverlayStandalone
                    captionsData={captionsData}
                    style={captionStyle}
                    getCurrentTime={() => videoRef.current?.currentTime || 0}
                    animationRequired={!!captionStyle?.animation && isPlaying}
                    canvasWidth={videoRef.current.videoWidth || 0}
                    canvasHeight={videoRef.current.videoHeight || 0}
                  />
                )}

              {!(isLoading || showLoader || isOutputLoading) && (
                <>
                  <VideoElementOverlay
                    isVisible={!isPlaying && showPauseOverlay}
                  />

                  <VideoElementPlayIconOverlay
                    onClick={togglePlay}
                    style={{
                      visibility: isPlaying ? 'hidden' : 'visible',
                      opacity: isPlaying ? 0 : 1,
                      cursor: cursorStyle,
                      pointerEvents:
                        cursorStyle === 'default' ? 'none' : 'auto',
                    }}
                  >
                    <Icon
                      size="large"
                      appearance="white"
                      name="Actions-Play"
                      style={{
                        width: isHovered ? '65px' : '60px',
                        height: isHovered ? '65px' : '60px',
                      }}
                    />
                  </VideoElementPlayIconOverlay>
                </>
              )}
            </VideoContentWrapper>
          </VideoElementContainer>
        ) : null}

        {videoUrl ? (
          <ControlsRow>
            <PlayIconContainer
              onClick={isOutputLoading ? undefined : togglePlay}
              sx={{
                cursor: isOutputLoading ? 'default' : 'pointer',
                opacity: isOutputLoading ? 0.48 : 1,
              }}
            >
              <Icon
                name={isPlaying ? 'Actions-Pause' : 'Actions-Play'}
                size="medium"
                appearance="neutral"
                color={
                  isOutputLoading
                    ? colorScheme.controlsDisabledColor
                    : colorScheme.controlsColor
                }
              />
            </PlayIconContainer>
            <ProgressBarContainer
              onClick={isOutputLoading ? undefined : handleProgressClick}
              sx={{
                cursor: isOutputLoading ? 'default' : 'pointer',
              }}
            >
              <ProgressBar
                colorScheme={colorScheme}
                sx={{
                  backgroundColor: isOutputLoading
                    ? colorScheme.controlsDisabledColor
                    : colorScheme.progressTrackColor,
                  opacity: isOutputLoading ? 0.48 : 1,
                }}
              >
                <ProgressFill
                  width={`${
                    videoRef.current?.duration
                      ? (currentTime / videoRef.current.duration) * 100
                      : 0
                  }%`}
                  colorScheme={colorScheme}
                  sx={{
                    backgroundColor: isOutputLoading
                      ? colorScheme.controlsDisabledColor
                      : colorScheme.progressColor,
                    opacity: isOutputLoading ? 0.48 : 1,
                  }}
                />
              </ProgressBar>
            </ProgressBarContainer>
            <TimeInfoContainer
              size="md"
              color={
                isOutputLoading
                  ? colorScheme.controlsDisabledColor
                  : colorScheme.controlsColor
              }
              sx={{opacity: isOutputLoading ? 0.48 : 1}}
            >
              {videoRef.current?.duration
                ? `${formatTime(currentTime)}/${formatTime(
                    videoRef.current?.duration
                  )}`
                : INITIAL_VIDEO_TIMESTAMP}
            </TimeInfoContainer>
            {controls.map(renderControl)}
          </ControlsRow>
        ) : null}
      </VideoContainer>
    </Container>
  );
}

export default VideoDisplay;
